/**
 * DashboardNav — barra global de navegação dos painéis.
 *
 * Cabeçalho IDÊNTICO nos dashboards de mediador e admin:
 *   [Mediador | Admin]  + Sair
 * com highlight do que está sendo usado/logado.
 *
 * Login por SENHA (role vem de ADMIN_TOKENS, lida em /mediadores/eu):
 *   - não logado → clicar entra na área (o gate manda pro /admin/login).
 *   - logado na MESMA role → não faz nada (já está nela).
 *   - logado em role DIFERENTE → re-login pra aquela role.
 *
 * Área do cliente (Acompanhar / Minha Conta) mantém o comportamento próprio.
 * NUNCA aparece em páginas públicas (home, sobre, faq, etc.).
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { User, ClipboardCheck, Settings, FileSearch, LogOut, BadgeCheck } from "lucide-react";
import { getSessionToken } from "../lib/api";
import { clearAdminToken, getAdminToken } from "../lib/admin-auth";

type Area = "cliente" | "mediador" | "admin";

// ── Páginas públicas (não mostram o nav) ──────────────────────────────────
function rotaPublica(path: string): boolean {
  const PUBLICAS = [
    "/", "/empresas", "/em-construcao", "/sobre", "/impacto", "/para-empresas",
    "/faq", "/blog", "/termos", "/privacidade", "/contrato",
    "/fluxo-ia", "/mapa", "/mapa-empresa", "/indicar", "/caso",
  ];
  return PUBLICAS.some((p) => path === p || path.startsWith(p + "/"));
}

function detectarArea(path: string): Area {
  if (path.startsWith("/admin/validacao") || path.startsWith("/admin/curadoria")) {
    return "mediador";
  }
  if (path === "/admin" || path.startsWith("/admin/")) {
    return "admin";
  }
  return "cliente";
}

// ── Botões do cliente (mantidos) ──────────────────────────────────────────
const PAINEIS_CLIENTE = [
  {
    id: "acompanhar", label: "Acompanhar", href: "/acompanhar",
    icon: FileSearch, color: "#22d3ee",
    descricao: "Acompanhar caso pelo protocolo",
    match: (p: string) => p.startsWith("/acompanhar") || p.startsWith("/resultado") || p.startsWith("/acordo"),
  },
  {
    id: "minha-conta", label: "Minha Conta", href: "/minha-conta",
    icon: User, color: "#4ade80",
    descricao: "Sua conta (opt-in): histórico + notificações",
    match: (p: string) => p.startsWith("/minha-conta") || p.startsWith("/configuracoes"),
  },
];

// ── Dashboards admin/mediador ────────────────────────────────────────────
const MEDIADOR = {
  id: "mediador" as const, label: "Mediador", href: "/admin/validacao",
  icon: ClipboardCheck, color: "#fbbf24",
};
const ADMIN = {
  id: "admin" as const, label: "Admin", href: "/admin",
  icon: Settings, color: "#a78bfa",
};

export default function DashboardNav() {
  const [location, navigate] = useLocation();
  const [role, setRole] = useState<string | null>(null);
  const [modo, setModo] = useState<string>("");

  const area = detectarArea(location);
  const ehDashboardAdmin = area === "mediador" || area === "admin";
  // Token atual muda no login/logout → re-busca a role. Antes a role ficava
  // "presa" de uma sessão anterior (cache module-level), o que fazia o header
  // errar o highlight e re-disparar o login em loop ao entrar como admin.
  const tokenAdmin = getAdminToken();

  // Descobre a role logada. Re-busca quando entra na área admin OU o token muda.
  useEffect(() => {
    if (!ehDashboardAdmin) {
      setRole(null);
      return;
    }
    let vivo = true;
    fetch("/api/v1/admin/mediadores/eu")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!vivo) return;
        setRole((d?.role as string) ?? null);
        setModo((d?.modo as string) ?? "");
      })
      .catch(() => { /* não logado → role null */ });
    return () => { vivo = false; };
  }, [ehDashboardAdmin, tokenAdmin]);

  // Esconde em páginas públicas E na tela de login (senão o header de painéis
  // aparece sobre o formulário e confunde o fluxo — parece "entrar duas vezes").
  if (rotaPublica(location) || location.startsWith("/admin/login")) return null;

  function irParaArea(alvo: "mediador" | "admin") {
    const destino = alvo === "mediador" ? MEDIADOR.href : ADMIN.href;
    const jaNaArea = area === alvo;
    if (jaNaArea) return;                       // já está nesse dashboard → nada
    if (modo === "fallback_dev" || !role) {     // dev ou não logado → entra (gate trata)
      navigate(destino);
      return;
    }
    if (role === alvo || (alvo === "mediador" && role === "admin")) {
      navigate(destino);                        // já tem acesso a essa role
      return;
    }
    // role diferente → re-login pra aquela role
    navigate(`/admin/login?redirect=${encodeURIComponent(destino)}`);
  }

  function sair() {
    clearAdminToken();
    navigate("/");
  }

  return (
    <nav
      aria-label="Painéis"
      className="sticky top-0 z-40 w-full border-b border-white/8 backdrop-blur-md"
      style={{ background: "rgba(15, 23, 42, 0.75)" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 flex items-center justify-between gap-2">
        {/* Logo → home */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-sm font-bold text-white/90 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-md px-2 py-1"
          title="Voltar pra página inicial"
        >
          ACORDEI
          <span className="text-white/40 ml-1 font-normal">·</span>
          <span className="text-[10px] uppercase tracking-wider text-white/40 ml-1 font-semibold">
            Painéis
          </span>
        </button>

        {ehDashboardAdmin ? (
          <div className="inline-flex items-center gap-2">
            <div
              role="tablist"
              className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/8"
            >
              {[MEDIADOR, ADMIN].map((p) => {
                const isAtivo = area === p.id;
                const Icon = p.icon;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={isAtivo}
                    aria-current={isAtivo ? "page" : undefined}
                    onClick={() => irParaArea(p.id)}
                    title={p.label}
                    className={
                      "inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold " +
                      "transition-all focus-visible:outline-none focus-visible:ring-2 " +
                      "focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 " +
                      (isAtivo ? "shadow-lg cursor-default" : "text-white/50 hover:text-white hover:bg-white/[0.04]")
                    }
                    style={
                      isAtivo
                        ? {
                            background: `linear-gradient(135deg, ${p.color}33, ${p.color}55)`,
                            border: `1px solid ${p.color}66`,
                            color: p.color,
                          }
                        : { border: "1px solid transparent" }
                    }
                  >
                    <Icon className="w-3.5 h-3.5" aria-hidden={true} />
                    <span className="hidden sm:inline">{p.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/meu-perfil")}
              title="Meu perfil de mediador"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold text-white/50 hover:text-white hover:bg-white/[0.04] border border-transparent transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            >
              <BadgeCheck className="w-3.5 h-3.5" aria-hidden={true} />
              <span className="hidden sm:inline">Meu perfil</span>
            </button>
            <button
              type="button"
              onClick={sair}
              title="Sair (voltar pra página inicial)"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold text-white/50 hover:text-white hover:bg-white/[0.04] border border-transparent transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden={true} />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        ) : (
          <ClienteBotoes location={location} navigate={navigate} />
        )}
      </div>
    </nav>
  );
}

function ClienteBotoes({
  location,
  navigate,
}: {
  location: string;
  navigate: (to: string) => void;
}) {
  const temSessao = typeof window !== "undefined" && !!getSessionToken();
  const visiveis = PAINEIS_CLIENTE.filter((p) => p.id !== "minha-conta" || temSessao);
  const ativo = visiveis.find((p) => p.match(location))?.id ?? null;
  return (
    <div
      role="tablist"
      className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/8"
    >
      {visiveis.map((p) => {
        const isAtivo = p.id === ativo;
        const Icon = p.icon;
        return (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={isAtivo}
            aria-current={isAtivo ? "page" : undefined}
            onClick={() => !isAtivo && navigate(p.href)}
            title={p.descricao}
            className={
              "inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold " +
              "transition-all focus-visible:outline-none focus-visible:ring-2 " +
              "focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 " +
              (isAtivo ? "shadow-lg cursor-default" : "text-white/50 hover:text-white hover:bg-white/[0.04]")
            }
            style={
              isAtivo
                ? {
                    background: `linear-gradient(135deg, ${p.color}33, ${p.color}55)`,
                    border: `1px solid ${p.color}66`,
                    color: p.color,
                  }
                : { border: "1px solid transparent" }
            }
          >
            <Icon className="w-3.5 h-3.5" aria-hidden={true} />
            <span className="hidden sm:inline">{p.label}</span>
          </button>
        );
      })}
    </div>
  );
}
